import { Bookmark, ChevronRight, Images, LayoutDashboard, Move, type LucideIcon } from 'lucide-react';
import { isLite } from '../../lib/devicePerf';
import { useDevice } from '../../store/device';
import { useLayoutEditor } from '../../store/layoutEditor';
import { useState } from 'react';
import { MAX_CUSTOM_CSS } from '../../lib/sanitizeCss';
import { LAYOUTS, SCALE_STEPS, THEMES, TRANSITIONS, useSettings } from '../../store/settings';
import { buttonClass, Choice, ghostButtonClass, Section } from './controls';
import { formatInterval } from './SlideshowTab';

const EXAMPLE = `/* Examples */
.glass { border-radius: 0.5rem; }
:root { --accent: #f472b6; }`;

/** Settings → Appearance → Layout: the arrangement of widgets over the photos. */
export function LayoutTab() {
  const layout = useSettings((s) => s.layout);
  const layoutPortrait = useSettings((s) => s.layoutPortrait);
  const photosOnly = useSettings((s) => s.photosOnly);
  const update = useSettings((s) => s.update);
  const startEditing = useLayoutEditor((s) => s.start);
  const usesCustom = layout === 'custom' || layoutPortrait === 'custom';
  return (
    <>
      <Section title="Current layout">
        <Choice options={LAYOUTS} value={layout} onChange={(v) => update('layout', v)} />
      </Section>
      {usesCustom && (
        <Section
          title="Custom layout"
          description="Move and resize the widgets on the screen itself. Landscape and portrait screens each keep their own arrangement."
        >
          {photosOnly ? (
            <p className="muted text-sm">Photos only is on (Settings → Widgets), so there are no widgets to arrange.</p>
          ) : (
            <button type="button" className={buttonClass} onClick={startEditing}>
              <Move className="h-4 w-4" aria-hidden /> Arrange widgets on screen
            </button>
          )}
        </Section>
      )}
      <Section
        title="On portrait screens"
        description="Used automatically when the screen is taller than it is wide – a rotated tablet or a portrait-mounted TV."
      >
        <Choice
          options={[
            ...LAYOUTS.filter((l) => l.id.startsWith('portrait') || l.id === 'custom'),
            { id: 'same' as const, name: 'Same as above', description: 'Don’t switch' },
          ]}
          value={layoutPortrait}
          onChange={(v) => update('layoutPortrait', v)}
          columns={3}
        />
      </Section>
    </>
  );
}

/** Settings → Appearance: links to Layout, Saved layouts and Slideshow, then theme, size, effects and custom CSS. */
export function AppearanceTab({ go }: { go: (tab: string) => void }) {
  const { effects, setEffects } = useDevice();
  const layout = useSettings((s) => s.layout);
  const presets = useSettings((s) => s.presets.length);
  const media = useSettings((s) => s.media);
  const theme = useSettings((s) => s.theme);
  const display = useSettings((s) => s.display);
  const customCss = useSettings((s) => s.customCss);
  const update = useSettings((s) => s.update);
  const reset = useSettings((s) => s.reset);
  const [confirmReset, setConfirmReset] = useState(false);

  const rows: Array<{ id: string; name: string; icon: LucideIcon; summary: string }> = [
    { id: 'layout', name: 'Layout', icon: LayoutDashboard, summary: LAYOUTS.find((l) => l.id === layout)?.name ?? '' },
    { id: 'presets', name: 'Saved layouts', icon: Bookmark, summary: presets ? `${presets} saved` : 'Save this whole setup to switch back later' },
    {
      id: 'slideshow',
      name: 'Slideshow',
      icon: Images,
      summary: `${formatInterval(media.intervalSec)} per slide · ${TRANSITIONS.find((t) => t.id === media.transition)?.name ?? ''}`,
    },
  ];

  return (
    <>
      <ul className="mb-8 space-y-2">
        {rows.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => go(r.id)}
              className="flex w-full items-center gap-3 rounded-xl border border-[var(--border)] p-3 text-left transition hover:border-[var(--fg-muted)]"
            >
              <r.icon className="h-5 w-5 shrink-0 opacity-80" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">{r.name}</span>
                <span className="muted block truncate text-xs">{r.summary}</span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
      <Section
        title="Display size"
        description="Size of the clock, widgets and news. Auto keeps the same proportions on any screen, from a tablet to a 4K TV."
      >
        <Choice
          options={[
            { id: 'auto', name: 'Auto', description: 'Scales with the screen' },
            { id: 'fixed', name: 'Fixed', description: 'Same size everywhere' },
          ]}
          value={display.scaleMode}
          onChange={(scaleMode) => update('display', { scaleMode })}
        />
        <div className="grid grid-cols-5 gap-2" role="radiogroup" aria-label="Size">
          {SCALE_STEPS.map((s) => (
            <button
              key={s.value}
              role="radio"
              aria-checked={display.scale === s.value}
              onClick={() => update('display', { scale: s.value })}
              className={`rounded-xl border py-2 text-sm font-medium transition ${
                display.scale === s.value ? 'border-[var(--accent)] bg-[var(--accent)]/10' : 'border-[var(--border)]'
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      </Section>
      <Section
        title="Visual effects"
        description="For this screen only. Lite uses solid panels instead of frosted glass and a Full HD demo video – smoother and more reliable on TV sticks such as Fire TV. Auto picks Lite on TVs and low-memory devices."
      >
        <Choice
          options={[
            { id: 'auto', name: 'Auto', description: isLite('auto') ? 'Lite on this screen' : 'Full on this screen' },
            { id: 'full', name: 'Full', description: 'Frosted glass, 4K demo' },
            { id: 'lite', name: 'Lite', description: 'Solid panels, Full HD demo' },
          ]}
          value={effects}
          onChange={setEffects}
          columns={3}
        />
      </Section>
      <Section title="Theme">
        <Choice options={THEMES} value={theme} onChange={(v) => update('theme', v)} columns={3} />
      </Section>
      <Section
        title="Custom CSS"
        description="Advanced: override any style. Changes apply live. Imports, scripts and non-HTTPS images are removed for safety."
      >
        <textarea
          value={customCss}
          onChange={(e) => update('customCss', e.target.value.slice(0, MAX_CUSTOM_CSS))}
          placeholder={EXAMPLE}
          spellCheck={false}
          rows={10}
          className="w-full rounded-lg border border-[var(--border)] bg-black/30 p-3 font-mono text-xs outline-none focus:border-[var(--accent)]"
          aria-label="Custom CSS"
        />
        <div className="muted text-right text-xs">
          {customCss.length.toLocaleString()} / {MAX_CUSTOM_CSS.toLocaleString()}
        </div>
      </Section>
      <Section title="Reset">
        {confirmReset ? (
          <div className="flex gap-2">
            <button
              className={`${ghostButtonClass} border-red-400 text-red-400`}
              onClick={() => {
                reset();
                setConfirmReset(false);
              }}
            >
              Yes, reset all settings
            </button>
            <button className={ghostButtonClass} onClick={() => setConfirmReset(false)}>
              Cancel
            </button>
          </div>
        ) : (
          <button className={ghostButtonClass} onClick={() => setConfirmReset(true)}>
            Reset settings on this screen
          </button>
        )}
      </Section>
    </>
  );
}
